/* eslint-disable no-nested-ternary */
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Select from 'react-select';

import { showErrorToast } from '../../../../shared/utils/toastManager';
import {
  createParentCalendar,
  createParentChildCalendar,
  createSavedWorkout,
  setCreateModalProgramType,
  setProgramType,
  fetchSavedWorkouts,
} from '../../ducks/calendarActions';

const Container = styled('div')`
  width: 100%;
  margin: 0 auto;
  padding: ${(props) => (props.isModal ? '20px' : '100px 20px')};
  background: white;
  min-height: ${(props) => (props.isModal ? 'auto' : '100vh')};

  @media (max-width: 768px) {
    padding: ${(props) => (props.isModal ? '16px' : '60px 16px')};
  }

  @media (max-width: 480px) {
    padding: ${(props) => (props.isModal ? '12px' : '40px 12px')};
  }
`;

const ContentWrapper = styled('div')`
  max-width: ${(props) => (props.isModal ? '100%' : '800px')};
  min-width: ${(props) => (props.isModal ? 'unset' : '700px')};
  margin: 0 auto;
  width: 100%;

  @media (max-width: 768px) {
    min-width: unset;
    max-width: 100%;
  }
`;

const Breadcrumbs = styled('div')`
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: #999;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    font-size: 13px;
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
    margin-bottom: 16px;
  }
`;

const BreadcrumbSeparator = styled('span')`
  margin: 0 8px;
  color: #ccc;
`;

const BreadcrumbButton = styled('button')`
  background: none;
  border: none;
  padding: 0;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 700;
  color: ${(props) => (props.disabled ? '#aaa' : '#999')};
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  text-decoration: none;
  pointer-events: ${(props) => (props.disabled ? 'none' : 'auto')};

  &:hover {
    text-decoration: ${(props) => (props.disabled ? 'none' : 'underline')};
  }

  &:focus {
    outline: ${(props) => (props.disabled ? 'none' : '2px solid #4A90E2')};
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    font-size: 13px;
  }

  @media (max-width: 480px) {
    font-size: 12px;
  }
`;

const Title = styled('h1')`
  font-family: 'Nunito Sans';
  font-size: 32px;
  font-weight: 700;
  color: #333;
  margin: 0 0 8px 0;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 28px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

const Subtitle = styled('p')`
  font-family: 'Nunito Sans';
  font-size: 16px;
  color: #666;
  margin: 0 0 40px 0;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 14px;
    margin: 0 0 32px 0;
  }

  @media (max-width: 480px) {
    font-size: 13px;
    margin: 0 0 24px 0;
  }
`;

const FormGroup = styled('div')`
  margin-bottom: 24px;
  
  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
  
  @media (max-width: 480px) {
    margin-bottom: 16px;
  }
`;

const Label = styled('label')`
  display: block;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
  
  @media (max-width: 768px) {
    font-size: 13px;
    margin-bottom: 6px;
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
    margin-bottom: 4px;
  }
`;

const Input = styled('input')`
  width: 100%;
  padding: 8px 16px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-family: 'Nunito Sans';
  font-size: 16px;
  color: #333;
  
  &::placeholder {
    color: #bbb;
  }
  
  &:focus {
    outline: none;
    border-color: #666;
  }
  
  @media (max-width: 768px) {
    padding: 10px 14px;
    font-size: 16px; /* Keep 16px to prevent zoom on iOS */
  }
  
  @media (max-width: 480px) {
    padding: 8px 12px;
  }
`;

const SelectContainer = styled('div')`
  .react-select__control {
    border: 1px solid #ddd;
    border-radius: 4px;
    min-height: 44px;
    box-shadow: none;
    font-family: 'Nunito Sans';

    &:hover {
      border-color: #666;
    }
    
    @media (max-width: 768px) {
      min-height: 48px; /* Larger touch target */
    }
  }

  .react-select__control--is-focused {
    border-color: #666;
    box-shadow: none;
  }

  .react-select__value-container {
    padding: 8px 16px;
    
    @media (max-width: 768px) {
      padding: 10px 14px;
    }
    
    @media (max-width: 480px) {
      padding: 8px 12px;
    }
  }

  .react-select__single-value {
    color: #333;
    font-family: 'Nunito Sans';
    font-size: 16px;
    
    @media (max-width: 768px) {
      font-size: 16px; /* Keep 16px to prevent zoom on iOS */
    }
  }

  .react-select__placeholder {
    color: #bbb;
    font-family: 'Nunito Sans';
    font-size: 16px;
    
    @media (max-width: 768px) {
      font-size: 16px; /* Keep 16px to prevent zoom on iOS */
    }
  }

  .react-select__menu {
    border: none;
    box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);
    border-radius: 4px;
  }

  .react-select__option {
    font-family: 'Nunito Sans';
    font-size: 16px;
    color: #333;
    cursor: pointer;
    padding: 8px 16px;
    
    &:active {
      background-color: #f5f5f5;
    }
    
    @media (max-width: 768px) {
      padding: 12px 16px; /* Larger touch target */
      font-size: 16px;
    }
  }

  .react-select__option--is-selected {
    background-color: #f5f5f5;
    color: #333;
  }

  .react-select__option--is-focused {
    background-color: #f5f5f5;
  }

  .react-select__indicator-separator {
    display: none;
  }

  .react-select__dropdown-indicator {
    color: #bbb;
    
    &:hover {
      color: #666;
    }
  }
`;

const ButtonContainer = styled('div')`
  display: flex;
  flex-direction: row;
  gap: 12px;
  margin-top: 40px;
  width: 100%;

  @media (max-width: 768px) {
    margin-top: 32px;
    gap: 10px;
  }

  @media (max-width: 480px) {
    margin-top: 24px;
    gap: 8px;
  }
`;

const Button = styled('button')`
  width: 100%;
  padding: 8px 24px;
  border-radius: 4px;
  font-family: 'Nunito Sans';
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  border: none;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.1);

  ${(props) => (props.variant === 'primary' ? `
    background: #444;
    color: white;
    &:hover {
      background: #555;
    }
    &:disabled {
      background: #ccc;
      cursor: not-allowed;
    }
  ` : `
    background: white;
    color: #444;
    border: 1px solid #ddd;
    &:hover {
      background: #f5f5f5;
    }
  `)}

  @media (max-width: 768px) {
    padding: 12px 24px;
    font-size: 14px;
    min-height: 48px; /* Larger touch target */
  }

  @media (max-width: 480px) {
    padding: 10px 20px;
    font-size: 13px;
    min-height: 44px;
  }
`;

const CalendarCreateContent = ({ isModal = false, onBack, closeModal }) => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(0);
  const [isCreatingCalendar, setIsCreatingCalendar] = useState(false);
  const [formData, setFormData] = useState({
    calendarType: 'calendar', // Default to calendar type
    calendarName: '',
    parentCalendar: '',
    parentCalendarName: '',
  });
  const parentCalendars = useSelector((state) => state.calendar.data.parentCalendars || []);
  const newlyCreatedCalendar = useSelector((state) => state.calendar.data.newlyCreatedCalendar);
  const newlyCreatedProgram = useSelector((state) => state.calendar.data.newlyCreatedProgram);
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const accountCode = currentUser?.accountCode;
  const selectedReduxCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxNonDateBasedProgram = useSelector(
    (state) => state.calendar.data.selectedReduxNonDateBasedProgram,
  );
  const calendarCreationError = useSelector((state) => state.calendar.data.calendarCreationError);
  const createModalProgramType = useSelector((state) => state.calendar.data.createModalProgramType);

  // Sync program type from URL on mount for deep-linking support
  useEffect(() => {
    if (location.pathname.includes('/program/create')) {
      dispatch(setCreateModalProgramType('free-form-program'));
    } else if (location.pathname.includes('/calendar/create')) {
      dispatch(setCreateModalProgramType('calendar'));
    }
  }, [location.pathname]);

  useEffect(() => {
    if (newlyCreatedProgram) {
      dispatch(fetchSavedWorkouts(accountCode));
    }
  }, [newlyCreatedProgram]);

  // // Watch for successful calendar creation and redirect/close
  // useEffect(() => {
  //   if (isCreatingCalendar && selectedReduxCalendar
  //       && Object.keys(selectedReduxCalendar).length > 0) {
  //     // Calendar was successfully created and selected
  //     if (isModal && closeModal) {
  //       closeModal(); // Close modal if in modal context
  //     } else {
  //       history.push('/build'); // Navigate if not in modal
  //     }
  //   }
  // }, [selectedReduxCalendar, isCreatingCalendar, history, isModal, closeModal]);

  const selectedCalendar = (createModalProgramType === 'calendar' && selectedReduxCalendar && Object.keys(selectedReduxCalendar).length > 0)
    ? selectedReduxCalendar
    : (selectedReduxNonDateBasedProgram
      && Object.keys(selectedReduxNonDateBasedProgram).length > 0)
      ? selectedReduxNonDateBasedProgram
      : null;

  useEffect(() => {
    if (isCreatingCalendar && selectedCalendar) {
    // Calendar was successfully created and selected
      if (isModal && closeModal) {
        closeModal(); // Close modal if in modal context
      } else {
        history.push('/build'); // Navigate if not in modal
      }
    }
  }, [
    selectedReduxCalendar,
    selectedReduxNonDateBasedProgram, // add this dependency
    isCreatingCalendar,
    history,
    isModal,
    closeModal,
  ]);

  // Watch for calendar creation errors and display toast
  useEffect(() => {
    if (isCreatingCalendar && calendarCreationError) {
      // Extract error message from the error object
      let errorMessage = 'Failed to create calendar. Please try again.';

      if (calendarCreationError) {
        errorMessage = calendarCreationError;
      }

      showErrorToast(errorMessage);

      // Reset the creating flag so user can try again
      setIsCreatingCalendar(false);
    }
  }, [calendarCreationError, isCreatingCalendar]);

  const steps = [
    {
      id: createModalProgramType === 'free-form-program' ? 'program-details' : 'calendar-details',
      title: createModalProgramType === 'free-form-program' ? 'Create New Program' : 'Calendar Details',
      component: 'CalendarDetailsForm',
    },
  ];

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCancel = () => {
    if (isModal && onBack) {
      onBack(); // Go back to previous slide in modal
      if (createModalProgramType === 'free-form-program') {
        dispatch(setCreateModalProgramType('calendar'));
      }
    } else {
      history.push('/build'); // Navigate away if not in modal
    }
  };

  const handleSave = () => {
    if (!currentUser?.accountCode) {
      return;
    }

    // Set flag to indicate calendar creation is in progress
    setIsCreatingCalendar(true);

    if (createModalProgramType === 'calendar') {
      dispatch(setProgramType('calendar'));
      const requestBody = {
        name: formData.calendarName,
        workoutVisibility: 0, // Default to "All" (value 0)
      };

      // Check if parent calendar is selected
      const hasParent = formData.parentCalendar
      && formData.parentCalendar !== '';

      if (hasParent) {
      // Call createParentChildCalendar
        requestBody.parentId = parseInt(formData.parentCalendar, 10);
        requestBody.parentName = formData.parentCalendarName;
        dispatch(createParentChildCalendar({
          accountCode: currentUser.accountCode,
          ...requestBody,
        }));
      } else {
      // Call createParentCalendar
        dispatch(createParentCalendar({
          accountCode: currentUser.accountCode,
          ...requestBody,
        }));
      }
    }
    if (createModalProgramType === 'free-form-program') {
      dispatch(setProgramType('free-form-program'));
      const requestBody = {
        programName: formData.calendarName,
        numberOfDays: 1,
      };
      dispatch(createSavedWorkout(accountCode, {
        ...requestBody,
      }));
    }
  };

  const isFormValid = () => formData.calendarName.trim() !== '';

  const getCalendarOptions = () => {
    const options = [
      { value: '', label: 'Select a calendar' },
    ];

    if (parentCalendars && parentCalendars.length > 0) {
      parentCalendars.forEach((calendar) => {
        options.push({
          value: calendar.id,
          label: calendar.name,
        });
      });
    }

    return options;
  };

  const getSelectedCalendarOption = () => {
    const options = getCalendarOptions();
    return options.find((option) => option.value === formData.parentCalendar) || null;
  };

  const renderCurrentStep = () => (
    <>
      <FormGroup>
        {createModalProgramType === 'free-form-program' ? (
          <Label>Program Name</Label>
        ) : (
          <Label>Calendar Name</Label>
        )}
        <Input
          type='text'
          value={formData.calendarName}
          onChange={(e) => handleInputChange('calendarName', e.target.value)}
          placeholder={createModalProgramType === 'free-form-program' ? 'Program name' : 'Calendar name'}
        />
      </FormGroup>

      {createModalProgramType !== 'free-form-program' && (
      <FormGroup>
        <Label>Parent Calendar</Label>
        <SelectContainer>
          <Select
            value={getSelectedCalendarOption()}
            onChange={(selectedOption) => {
              if (selectedOption) {
                handleInputChange('parentCalendar', selectedOption.value);
                handleInputChange('parentCalendarName', selectedOption.label);
              } else {
                // Clear both when selection is removed
                handleInputChange('parentCalendar', '');
                handleInputChange('parentCalendarName', '');
              }
            }}
            options={getCalendarOptions()}
            className='react-select'
            classNamePrefix='react-select'
            placeholder='Select a calendar'
            isSearchable
            isClearable
          />
        </SelectContainer>
      </FormGroup>
      )}
    </>
  );

  const currentStepData = steps[currentStep];

  return (
    <Container isModal={isModal}>
      <ContentWrapper isModal={isModal}>
        <Breadcrumbs>
          <BreadcrumbButton
            onClick={isModal ? (onBack || undefined) : () => history.push('/build')}
            disabled={isModal && !onBack}
          >
            Program Type
          </BreadcrumbButton>
          <BreadcrumbSeparator>/</BreadcrumbSeparator>
          {createModalProgramType === 'free-form-program' ? 'Program Details' : 'Calendar Details'}
        </Breadcrumbs>
        <Title>{currentStepData.title}</Title>
        {createModalProgramType !== 'free-form-program' && (
        <Subtitle>
          Provide the basic details for the calendar.
        </Subtitle>
        )}

        {renderCurrentStep()}

        <ButtonContainer>
          <Button variant='secondary' onClick={handleCancel}>
            Back
          </Button>
          <Button
            variant='primary'
            onClick={handleSave}
            disabled={!isFormValid()}
          >
            Save
          </Button>
        </ButtonContainer>
      </ContentWrapper>
    </Container>
  );
};

CalendarCreateContent.propTypes = {
  isModal: PropTypes.bool,
  onBack: PropTypes.func,
  closeModal: PropTypes.func,
};

CalendarCreateContent.defaultProps = {
  isModal: false,
  onBack: null,
  closeModal: null,
};

export default CalendarCreateContent;
